import { useState, useEffect, useCallback } from 'react'
import { api } from '../api'

/**
 * KBNL-141: 내가 시작한 백그라운드 잡 중 'running' 인 것을 주기 폴링해서
 * 목록 항목에 진행 스피너를 매칭할 수 있게 owner::name Set 으로 제공.
 *
 * 반환: { workbench: Set<owner::name>, aiCombine: Set<owner::name> }
 *  - workbench: step.kind 가 ai_combine 이 아닌 잡(워크벤치 변환) 키들
 *  - aiCombine: step.kind === 'ai_combine' (컴포지트 AI 조합) 키들
 *
 * @param {boolean} enabled     해당 탭이 활성일 때만 폴링(불필요 요청 방지)
 * @param {number}  intervalMs  폴링 주기(기본 6s)
 */
export function useRunningJobs(enabled = true, intervalMs = 6000) {
  const [running, setRunning] = useState(() => ({ workbench: new Set(), aiCombine: new Set() }))

  // 폴링 1회 — useCallback(빈 deps)로 안정 참조. setState 는 비동기(fetch 이후)라
  // effect 동기 setState 규칙에 걸리지 않는다.
  const tick = useCallback(async () => {
    try {
      const r = await api.wbJobsRunning()
      const wb = new Set()
      const ai = new Set()
      for (const j of (r.jobs || [])) {
        if (!j?.name) continue
        const key = j.key || `${j.owner_user_id || ''}::${j.name}`
        if (j.kind === 'ai_combine') ai.add(key)
        else wb.add(key)
      }
      setRunning(prev => (setEq(prev.workbench, wb) && setEq(prev.aiCombine, ai))
        ? prev                                   // 내용 동일 → 리렌더 방지
        : { workbench: wb, aiCombine: ai })
    } catch { /* 폴링 실패 무시 */ }
  }, [])

  useEffect(() => {
    if (!enabled) {
      // 비활성 → 다음 틱에 비움 (effect 본문 동기 setState 회피)
      const t = setTimeout(() => setRunning(prev =>
        (prev.workbench.size || prev.aiCombine.size)
          ? { workbench: new Set(), aiCombine: new Set() }
          : prev), 0)
      return () => clearTimeout(t)
    }
    const run = () => { tick() }
    const t0 = setTimeout(run, 0)            // 즉시 1회
    const iv = setInterval(run, intervalMs)  // 주기 폴링
    return () => { clearTimeout(t0); clearInterval(iv) }
  }, [enabled, intervalMs, tick])

  return running
}

function setEq(a, b) {
  if (a.size !== b.size) return false
  for (const v of a) if (!b.has(v)) return false
  return true
}
